import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { components } from '@/types/api';

type GFICode = components['schemas']['GFICode'];


export function GFICodes() {
  const [gfiCodes, setGfiCodes] = useState<GFICode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const { idToken } = useCognitoAuth();

  useEffect(() => {
    if (!idToken) return;
    async function fetchGfiCodes() {
      try {
        const data = await api.get<GFICode[]>('/api/entities/gfi-codes', { errorCode: ErrorCode.FETCH_GFI_CODES, token: idToken! });
        setGfiCodes(data ?? []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch GFI codes');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchGfiCodes();
  }, [retryKey, idToken]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading GFI codes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">GFI Codes</h1>
          <p className="text-sm text-muted-foreground">General Federal Index codes for financial reporting</p>
        </div>
        <Button variant="default" size="sm">
          <i className="bi bi-plus"></i> Add GFI Code
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>GFI Codes ({gfiCodes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Code</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Name</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Category</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gfiCodes.map((gfi) => (
                  <tr key={gfi.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{gfi.code}</code></td>
                    <td className="py-3 px-4">{gfi.name}</td>
                    <td className="py-3 px-4">
                      <Badge variant="secondary">{gfi.category}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={gfi.is_active ? 'secondary' : 'default'}>
                        {gfi.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="sm">Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-info-circle"></i> About GFI Codes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            General Federal Index (GFI) codes are used for financial reporting and tax compliance in Canada.
            They classify accounts by their purpose in financial statements.
          </p>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li><strong>Assets</strong> - Resources owned by the business</li>
            <li><strong>Liabilities</strong> - Obligations owed by the business</li>
            <li><strong>Equity</strong> - Owner's interest in the business</li>
            <li><strong>Revenue</strong> - Income from business activities</li>
            <li><strong>Expenses</strong> - Costs of doing business</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
